import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Anchor, Plane, Truck, Loader2, CheckCircle2 } from "lucide-react";
import { TID } from "@/lib/testIds";
import { submitQuote } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const ROAD_IMG =
  "https://images.unsplash.com/photo-1761133381018-aed5063d22fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwYWVyaWFsfGVufDB8fHx8MTc4Mjc0MjEzOXww&ixlib=rb-4.1.0&q=85";

const schema = z.object({
  name: z.string().min(1, "Name required").max(120),
  company: z.string().max(160).optional().or(z.literal("")),
  email: z.string().email("Valid email required"),
  phone: z.string().min(4, "Phone required").max(40),
  origin: z.string().min(1, "Origin required").max(160),
  destination: z.string().min(1, "Destination required").max(160),
  shipment_type: z.enum(["air", "sea", "road"], { required_error: "Pick a mode" }),
  cargo_details: z.string().min(1, "Cargo details required").max(4000),
});

const inputCls =
  "rounded-none border-slate-300 bg-white focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:border-sky-500 h-12";
const labelCls = "font-semibold text-xs uppercase tracking-wider text-slate-700";

export default function QuoteForm() {
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { shipment_type: "sea" },
  });

  const shipment = watch("shipment_type");

  const onSubmit = async (values) => {
    try {
      const payload = { ...values, company: values.company || null };
      await submitQuote(payload);
      setSubmitted(true);
      toast.success("Quote request received. Our team will reach out shortly.");
      reset({ shipment_type: "sea" });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    }
  };

  return (
    <section
      id="quote"
      data-testid={TID.quote.section}
      className="bg-white border-t border-slate-200"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Left visual panel */}
        <div className="relative min-h-[400px] lg:min-h-full bg-blue-900 overflow-hidden grain">
          <img
            src={ROAD_IMG}
            alt="JMR Global Logistics road transport — freight truck on international highway"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/80 via-blue-950/60 to-transparent" />
          <div className="relative z-10 h-full p-10 md:p-16 lg:p-20 flex flex-col justify-between">
            <div className="reveal">
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-sky-400 mb-4">
                Request a Free Quote
              </p>
              <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white leading-[1.02]">
                Tell us<br />where it&apos;s<br /><span className="text-sky-400">going.</span>
              </h2>
              <p className="mt-6 text-slate-200 text-base md:text-lg max-w-md">
                Submit your shipment brief. We&apos;ll respond with route options, mode and indicative pricing within one business day.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 text-white reveal">
              {[
                { Icon: Anchor, label: "Sea" },
                { Icon: Plane, label: "Air" },
                { Icon: Truck, label: "Road" },
              ].map((m) => (
                <div key={m.label} className="border border-white/20 p-4 flex flex-col items-center gap-2">
                  <m.Icon className="w-5 h-5 text-sky-400" />
                  <span className="text-xs font-semibold uppercase tracking-wider">{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right form */}
        <div className="p-8 md:p-16 lg:p-20 bg-slate-50">
          {submitted ? (
            <div data-testid={TID.quote.success} className="h-full flex flex-col justify-center items-start gap-6 max-w-md">
              <div className="w-16 h-16 bg-sky-400 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-slate-900" />
              </div>
              <h3 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-slate-900">
                Quote received.
              </h3>
              <p className="text-slate-600">
                Thanks — our team will reach out within one business day with route options and pricing.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="bg-blue-900 text-white px-6 py-3 font-semibold hover:bg-blue-800 transition-colors"
              >
                Submit another request
              </button>
            </div>
          ) : (
            <form
              data-testid={TID.quote.form}
              onSubmit={handleSubmit(onSubmit)}
              className="max-w-2xl mx-auto"
              noValidate
            >
              <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">// SHIPMENT BRIEF</p>
              <h3 className="font-display font-bold text-2xl md:text-3xl text-slate-900 mb-8">Fill in the details below.</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-1">
                  <Label htmlFor="name" className={labelCls}>Name *</Label>
                  <Input data-testid={TID.quote.name} id="name" {...register("name")} className={`${inputCls} mt-2`} placeholder="John Doe" />
                  {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name.message}</p>}
                </div>
                <div className="md:col-span-1">
                  <Label htmlFor="company" className={labelCls}>Company</Label>
                  <Input data-testid={TID.quote.company} id="company" {...register("company")} className={`${inputCls} mt-2`} placeholder="Acme Logistics" />
                </div>
                <div className="md:col-span-1">
                  <Label htmlFor="email" className={labelCls}>Email *</Label>
                  <Input data-testid={TID.quote.email} id="email" type="email" {...register("email")} className={`${inputCls} mt-2`} placeholder="you@company.com" />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email.message}</p>}
                </div>
                <div className="md:col-span-1">
                  <Label htmlFor="phone" className={labelCls}>Phone *</Label>
                  <Input data-testid={TID.quote.phone} id="phone" {...register("phone")} className={`${inputCls} mt-2`} placeholder="+91 98 765 43210" />
                  {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone.message}</p>}
                </div>
                <div className="md:col-span-1">
                  <Label htmlFor="origin" className={labelCls}>Origin *</Label>
                  <Input data-testid={TID.quote.origin} id="origin" {...register("origin")} className={`${inputCls} mt-2`} placeholder="Mumbai, India" />
                  {errors.origin && <p className="text-xs text-red-600 mt-1">{errors.origin.message}</p>}
                </div>
                <div className="md:col-span-1">
                  <Label htmlFor="destination" className={labelCls}>Destination *</Label>
                  <Input data-testid={TID.quote.destination} id="destination" {...register("destination")} className={`${inputCls} mt-2`} placeholder="London, UK" />
                  {errors.destination && <p className="text-xs text-red-600 mt-1">{errors.destination.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <Label className={labelCls}>Shipment Type *</Label>
                  <RadioGroup
                    value={shipment}
                    onValueChange={(v) => setValue("shipment_type", v, { shouldValidate: true })}
                    className="mt-3 grid grid-cols-3 gap-3"
                  >
                    {[
                      { v: "air", label: "Air", Icon: Plane, tid: TID.quote.typeAir },
                      { v: "sea", label: "Sea", Icon: Anchor, tid: TID.quote.typeSea },
                      { v: "road", label: "Road", Icon: Truck, tid: TID.quote.typeRoad },
                    ].map((opt) => {
                      const active = shipment === opt.v;
                      return (
                        <label
                          key={opt.v}
                          data-testid={opt.tid}
                          className={`cursor-pointer flex items-center gap-3 border p-4 transition-all ${
                            active
                              ? "border-sky-500 bg-sky-50 text-slate-900"
                              : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
                          }`}
                        >
                          <RadioGroupItem value={opt.v} id={`r-${opt.v}`} className="border-slate-400" />
                          <opt.Icon className={`w-5 h-5 ${active ? "text-sky-600" : "text-slate-500"}`} />
                          <span className="font-semibold">{opt.label}</span>
                        </label>
                      );
                    })}
                  </RadioGroup>
                  {errors.shipment_type && <p className="text-xs text-red-600 mt-1">{errors.shipment_type.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="cargo" className={labelCls}>Cargo Details *</Label>
                  <Textarea
                    data-testid={TID.quote.cargo}
                    id="cargo"
                    rows={4}
                    {...register("cargo_details")}
                    className="rounded-none border-slate-300 bg-white focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:border-sky-500 mt-2"
                    placeholder="Describe your cargo: dimensions, weight, type, special handling, target dates..."
                  />
                  {errors.cargo_details && <p className="text-xs text-red-600 mt-1">{errors.cargo_details.message}</p>}
                </div>
              </div>

              <button
                data-testid={TID.quote.submit}
                type="submit"
                disabled={isSubmitting}
                className="mt-8 w-full md:w-auto inline-flex items-center justify-center gap-3 bg-blue-900 text-white font-semibold px-10 py-4 hover:bg-sky-400 hover:text-blue-900 transition-colors disabled:opacity-60 shadow-[6px_6px_0px_0px_rgba(56,189,248,1)] hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-[3px_3px_0px_0px_rgba(15,23,42,0.2)]"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  <>Submit Quote Request →</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
